/**
 * The Finance reads (Phase 1; docs/finance/phase-1-plan.md section 4).
 *
 * Every figure here is what the database returned, in integer paise. Nothing is added up, rounded or compared in the browser:
 * the ledger, the plan of the backfill and the diff are all worked out by `api.*` functions, and this file only asks.
 *   · api.finance_settings      (4.2)  billing start, payments-from, due day, the two switches, today
 *   · api.ledger_integrity      (4.11) the checks
 *   · api.ledger_backfill       (4.9)  DRY_RUN only: the desk never posts a backfill
 *   · api.ledger_diff           (4.10) ledger against the old balance and against the sheet
 *   · api.sign_ledger_diff      (4.10) the one write on the Ledger check page
 *   · api.suspicious_openings   (4.11) the rules
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDaysIso } from '@rtcom/domain';
import { rpc } from '../../lib/supabase';

export interface FinanceSettings {
  billingStarts: string; paymentsFrom: string; dueDay: number; ledgerPosting: boolean; ledgerReads: boolean; today: string;
}

export interface IntegrityCheck { code: string; ok: boolean; detail: string }
export interface LedgerIntegrity { checks: IntegrityCheck[] }

export interface BackfillDryRun {
  mode: 'DRY_RUN'; through: string; entries: number; lines: number;
  byType: Record<string, { count: number; paise: number }>;
  byLabel: { migration: number; old_khata: number; manual: number };
  trialBalance: { code: string; debit: number; credit: number }[]; balanced: boolean;
  diff: { customers: number; zero: number; nonZero: number; sumAbsPaise: number };
  sheet: { customers: number; differ: number; sumAbsPaise: number };
  thankYouFires: boolean;
}

export const EXPLANATIONS = ['SHEET_CREDIT_NOT_CARRIED', 'MERGED_SHEET_ACCOUNTS', 'NO_SHEET_HISTORY', 'GHEE_CHARGED_AGAIN', 'KHATA_OPENING', 'OWNER_CONFIRMED', 'OTHER'] as const;
export type Explanation = typeof EXPLANATIONS[number];

export interface LedgerDiffRow {
  customer_id: string; code: string; status: string; computed_paise: number; ledger_paise: number; ledger_vs_computed_paise: number;
  sheet_closing_paise: number | null; ledger_vs_sheet_paise: number | null; suggested: Explanation | null; signed: string | null; signed_note: string | null;
}

export const OPENING_RULES = ['ABOVE_PLAUSIBLE', 'NO_DELIVERIES', 'NO_SOURCE', 'MERGED_ACCOUNTS', 'NOT_LIVE', 'JOINED_AFTER'] as const;
export type OpeningRule = typeof OPENING_RULES[number];

export interface SuspiciousOpening {
  customer_id: string; code: string; status: string; opening_paise: number; effective_on: string; source_label: string | null; reference: string | null;
  first_bill_month: string | null; avg_month_bill_paise: number | null; plausible_paise: number | null; delivered_days: number | null; rules: OpeningRule[];
}

export function useFinanceSettings() {
  return useQuery({ queryKey: ['finance-settings'], staleTime: 60_000, queryFn: () => rpc<FinanceSettings>('finance_settings', {}) });
}

/**
 * When billing starts, read from Finance (R5: nothing in the desk hard-codes it any more). While the setting is loading
 * `startsOn` is null and every gate built on it stays closed, so nothing before the real date is offered by mistake.
 * `openingOn` is the day before billing starts: the day the opening balances are carried to.
 */
export function useBillingWindow() {
  const q = useFinanceSettings();
  const startsOn = q.data?.billingStarts ?? null;
  return { startsOn, startMonth: startsOn ? startsOn.slice(0, 7) : null, openingOn: startsOn ? addDaysIso(startsOn, -1) : null, ready: startsOn !== null, error: q.error };
}

export function useLedgerIntegrity(asOf: string | null, enabled = true) {
  return useQuery({ queryKey: ['ledger-integrity', asOf], enabled, queryFn: () => rpc<LedgerIntegrity>('ledger_integrity', { p_as_of: asOf }) });
}

export function useBackfillDryRun(through: string | null, enabled = true) {
  return useQuery({ queryKey: ['ledger-backfill-dry-run', through], enabled,
    queryFn: () => rpc<BackfillDryRun>('ledger_backfill', { p_mode: 'DRY_RUN', p_through: through, p_reason: null }) });
}

export function useLedgerDiff(asOf: string | null, onlyDifferences: boolean, customerId: string | null, enabled = true) {
  return useQuery({ queryKey: ['ledger-diff', asOf, onlyDifferences, customerId], enabled,
    queryFn: () => rpc<LedgerDiffRow[]>('ledger_diff', { p_as_of: asOf, p_only_differences: onlyDifferences, p_customer_id: customerId }) });
}

export function useSuspiciousOpenings(rule: OpeningRule | null, enabled = true) {
  return useQuery({ queryKey: ['suspicious-openings', rule], enabled, queryFn: () => rpc<SuspiciousOpening[]>('suspicious_openings', { p_rule: rule }) });
}

export function useSignLedgerDiff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { customerId: string; asOf: string; comparison: 'LEDGER_VS_COMPUTED' | 'LEDGER_VS_SHEET'; explanation: Explanation; note: string }) =>
      rpc<{ status?: string }>('sign_ledger_diff', { p_customer_id: v.customerId, p_as_of: v.asOf, p_comparison: v.comparison, p_explanation: v.explanation, p_note: v.note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ledger-diff'] }),
  });
}
